/**
 * W37 (C8; engine FR-E58, plan D170; ADR-0025) — a split PDF on the screens.
 *
 * - **`<SplitPanel>`**, in the document view: a **part** says it is one — *pages 3 – 4 of 6*
 *   of its original, with the way to the original; a **split original** lists its parts,
 *   each its pages and the way to it, and offers *Undo the split* to whoever may archive
 *   (`document:can_delete`): the parts are archived, the original's mark cleared, nothing
 *   deleted; an **uncertain** original says the engine was not sure where it divides, so
 *   it was read whole.
 * - **`<SplitMark>`**, in the register: a part's pages, an original's *Split* — one quiet
 *   badge beside the name, from the row itself (no request of its own).
 *
 * The split itself is asked for by the caller (`options.split: true` on a parse) and done by
 * the engine, never in the browser (`docs/legacy/prodoc-ui-vu.md` §4).
 */
import { Alert, Badge, Button, ConfirmDialog, useToast } from '@fiveox/ui'
import { useState } from 'react'

import { DOCUMENT, type ProDocClient, type ProDocDocument, type Verb } from './prodoc-client'
import { errorText } from './hooks'
import { pagesLabel, splitMarkOf, splitPagesOf } from './split-shapes'

export interface SplitPanelProps {
  client: ProDocClient
  doc: ProDocDocument
  /** The reader's verbs — *Undo the split* is the archive verb's. */
  can: (resource: string, verb: Verb) => boolean
  /** Open another document the host's way; without it, the parts and the original are named, not links. */
  onOpenDocument?: (document: ProDocDocument) => void
  /** The original as it now stands, after an undo. */
  onChanged?: (document: ProDocDocument) => void
}

export function SplitPanel({ client, doc, can, onOpenDocument, onChanged }: SplitPanelProps) {
  const { toast } = useToast()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = async (documentId: string) => {
    if (!onOpenDocument) return
    try {
      onOpenDocument(await client.documents.get(documentId))
    } catch (e) {
      toast({ message: errorText(e, 'That document could not be opened.'), tone: 'error' })
    }
  }

  const link = (documentId: string, label: string) =>
    onOpenDocument ? (
      <Button variant="link" className="h-auto px-0" onClick={() => void open(documentId)}>
        {label}
      </Button>
    ) : (
      <span>{label}</span>
    )

  const pages = splitPagesOf(doc.split_pages)
  if (pages && doc.split_from) {
    return (
      <Alert tone="info">
        <p>
          A part of a PDF that held several documents: {pagesLabel(pages)} of {link(doc.split_from.document_id, 'the original')}, which is kept as it
          was.
        </p>
      </Alert>
    )
  }

  if (doc.split_state === 'uncertain') {
    return (
      <Alert tone="warning">
        <p>The engine was not sure where this PDF divides, so nothing was cut: it was read whole. A person decides where its documents start.</p>
      </Alert>
    )
  }

  if (doc.split_state !== 'split') return null
  const parts = doc.split_parts ?? []
  const undoable = can(DOCUMENT, 'can_delete') && doc.status === 'stored'

  const undo = async () => {
    setBusy(true)
    setError(null)
    try {
      const { document, archived } = await client.documents.undoSplit(doc.id)
      setConfirming(false)
      toast(`Split undone: ${archived.length === 1 ? 'its part was' : `its ${archived.length} parts were`} archived.`)
      onChanged?.(document)
    } catch (e) {
      setError(errorText(e, 'The split could not be undone.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Alert tone="info">
      <p>
        Split into {parts.length === 1 ? 'one part' : `${parts.length} parts`}, each its own document. This file is kept as it was: it takes no new version
        while it stands split.
      </p>
      {parts.length > 0 && (
        <ul className="mt-2 space-y-1" aria-label="Parts">
          {parts.map((part, index) => (
            <li key={part.id} className="flex flex-wrap items-center gap-2 text-sm">
              {link(part.id, `Part ${index + 1}`)}
              <span className="text-muted-foreground">{pagesLabel(part.pages)}</span>
              {part.status !== 'stored' && <Badge size="sm">{part.status}</Badge>}
            </li>
          ))}
        </ul>
      )}
      {undoable && (
        <Button variant="link" onClick={() => setConfirming(true)}>
          Undo the split
        </Button>
      )}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Undo the split?"
        tone="danger"
        confirmLabel="Undo the split"
        cancelLabel="Keep the parts"
        busy={busy}
        error={error}
        onConfirm={() => void undo()}
      >
        <p>Its parts are archived and this file is no longer marked split. Nothing is deleted; splitting it again makes new parts.</p>
      </ConfirmDialog>
    </Alert>
  )
}

/** The register's badge for a split document: a part's pages, an original's mark. */
export function SplitMark({ row }: { row: Pick<ProDocDocument, 'split_state' | 'split_pages'> }) {
  const mark = splitMarkOf(row)
  if (!mark) return null
  return (
    <Badge size="sm" tone={row.split_state === 'uncertain' ? 'warning' : 'neutral'}>
      {mark}
    </Badge>
  )
}
